import { useMemo } from "react";
import {
  useGetResidentShiftHandoverQuery,
  useGetShiftHandoverIndexQuery,
} from "app/services/api/shiftHandoverApi";
import usePagination from "ui/molecules/pagination/usePagination";
import { ShiftHandoverStatusEnum } from "../../../services/api/types/shiftHandover/enums/ShiftHandoverStatusEnum";
import { ShiftHandoverTaskClass } from "../../../services/api/types/shiftHandover/classes/ShiftHandoverTaskClass";
import {
  generateBooleanCountLogs,
  generateGroupedTotalLogs,
} from "ui/utils/logUtils";
import { formatEnumText } from "ui/utils/nameUtil";
import { useCalendarEventTypes } from "app/features/calendar/hooks/useCalendarEventTypes";

interface Props {
  residentId: number;
}

export function useHandleShiftHandovers({ residentId }: Props) {
  const { currentPage, handlePageChange } = usePagination();

  const { friendlyNames } = useCalendarEventTypes();

  const { data: indexData, isLoading: isIndexLoading } =
    useGetShiftHandoverIndexQuery(
      {
        filter: {
          resident_id: residentId!,
          status: ShiftHandoverStatusEnum.SUBMITTED,
        },
        page: currentPage,
        limit: 3,
        sort: "-submitted_at",
      },
      { skip: !residentId },
    );

  const { data: draftData, isLoading: isDraftIndexLoading } =
    useGetShiftHandoverIndexQuery(
      {
        filter: {
          resident_id: residentId!,
          status: ShiftHandoverStatusEnum.DRAFT,
        },
        limit: 3,
        page: 1,
      },
      { skip: !residentId },
    );

  const draftItem = useMemo(() => draftData?.data?.[0], [draftData]);
  const draftExists = useMemo(() => !!draftItem, [draftItem]);

  const { data: handoverDataRaw, isLoading: isHandoverLoadingRaw } =
    useGetResidentShiftHandoverQuery(residentId!, {
      skip: !residentId || isDraftIndexLoading || draftExists,
    });

  const handoverData = draftExists ? draftItem : handoverDataRaw;
  const isHandoverLoading = isDraftIndexLoading || isHandoverLoadingRaw;

  const parsedOverdueAppointments = useMemo(() => {
    if (!friendlyNames) return []; // wait until mapping is loaded
    return draftExists
      ? ShiftHandoverTaskClass.fromShiftHandoverAppointmentModelsArray(
          draftItem!.overdue_appointments,
          friendlyNames,
        )
      : ShiftHandoverTaskClass.fromAppointmentModelsArray(
          handoverDataRaw?.overdue_appointments ?? [],
          friendlyNames,
        );
  }, [
    draftExists,
    draftItem?.overdue_appointments,
    handoverDataRaw?.overdue_appointments,
    friendlyNames,
  ]);

  const parsedDueTodayAppointments = useMemo(() => {
    if (!friendlyNames) return [];
    return draftExists
      ? ShiftHandoverTaskClass.fromShiftHandoverAppointmentModelsArray(
          draftItem!.due_today_appointments,
          friendlyNames,
        )
      : ShiftHandoverTaskClass.fromAppointmentModelsArray(
          handoverDataRaw?.due_today_appointments ?? [],
          friendlyNames,
        );
  }, [
    draftExists,
    draftItem?.due_today_appointments,
    handoverDataRaw?.due_today_appointments,
    friendlyNames,
  ]);

  const fluidIntakeLogs = useMemo(() => {
    const records = handoverData?.fluid_intake_records;
    return generateGroupedTotalLogs({
      records,
      groupByKey: "intake_method",
      valueKey: "consumed_amount",
      unit: " ml",
      formatKey: formatEnumText,
    });
  }, [handoverData?.fluid_intake_records]);

  const fluidOutputLogs = useMemo(() => {
    const records = handoverData?.fluid_output_records;
    return generateGroupedTotalLogs({
      records,
      groupByKey: "output_type",
      valueKey: "output_volume",
      formatKey: formatEnumText,
    });
  }, [handoverData?.fluid_output_records]);

  const bowelMovementLogs = useMemo(() => {
    const records = handoverData?.bowel_movement_records;
    return generateBooleanCountLogs({
      records,
      booleanKeys: ["bowels_opened", "enema_suppositories_done"],
      labels: ["Bowels Opened", "Enema/Suppositories Done"],
    });
  }, [handoverData?.bowel_movement_records]);

  const assessmentHistoryLogs = (handover: any) => ({
    fluidIntakeLogs: generateGroupedTotalLogs({
      records: handover.fluid_intake_records,
      groupByKey: "intake_method",
      valueKey: "consumed_amount",
      unit: "ml",
      formatKey: formatEnumText,
    }),
    fluidOutputLogs: generateGroupedTotalLogs({
      records: handover.fluid_output_records,
      groupByKey: "output_type",
      valueKey: "output_volume",
      unit: "ml",
      formatKey: formatEnumText,
    }),
    bowelMovementLogs: generateBooleanCountLogs({
      records: handover.bowel_movement_records,
      booleanKeys: ["bowels_opened", "enema_suppositories_done"],
      labels: ["Bowels Opened", "Enema/Suppositories Done"],
    }),
  });

  return {
    indexData,
    isIndexLoading,
    parsedOverdueAppointments,
    parsedDueTodayAppointments,
    handoverData,
    isHandoverLoading,
    fluidIntakeLogs,
    fluidOutputLogs,
    bowelMovementLogs,
    assessmentHistoryLogs,
    handlePageChange,
  };
}
